import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Switch, Table, Tag } from 'antd';
import { AccessService } from '../../service/api/access-service';
import { usePermission } from '../../core/utils/PermissionContext';
import { MENU_ICON_NAMES, renderMenuIcon } from '../../core/utils/menuIcons';
import { buildCatalogTree } from './access-utils';

const CODE_RULES = [
  { required: true, message: 'Code is required' },
  { max: 50 },
  { pattern: /^[A-Z0-9_]+$/, message: 'Use UPPER_CASE letters, digits and _ only' },
];

const iconOptions = MENU_ICON_NAMES.map(name => ({
  value: name,
  label: (
    <span className="d-inline-flex align-items-center gap-2">
      {renderMenuIcon(name)} {name}
    </span>
  ),
}));

const CatalogTab = ({ canCreate, canEdit }) => {
  const { reload: reloadMyAccess } = usePermission();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const [moduleEdit, setModuleEdit] = useState(null);
  const [pageEdit, setPageEdit] = useState(null);
  const [permPage, setPermPage] = useState(null);
  const [permEdit, setPermEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const [moduleForm] = Form.useForm();
  const [pageForm] = Form.useForm();
  const [permForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await AccessService.getCatalog();
      setCatalog(result);
      setExpandedKeys(prev => (prev.length ? prev : (result?.modules || []).map(m => `m-${m.id}`)));
    } catch (_e) {
      // toast shown by httpInterceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const afterSave = async () => {
    await load();
    reloadMyAccess(); // menu may have changed
  };

  const runSave = async (fn, close) => {
    setSaving(true);
    try {
      await fn();
      close();
      await afterSave();
    } catch (_e) {
      // toast shown by httpInterceptor (duplicate code/route, 409 conflict, ...)
    } finally {
      setSaving(false);
    }
  };

  const tree = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = item =>
      !term ||
      [item.name, item.code, item.routePattern].some(v => (v || '').toLowerCase().includes(term));
    const filterNode = node => {
      const item = node.type === 'module' ? node.module : node.page;
      if (!showInactive && !item.isActive) return null;
      const children = (node.children || []).map(filterNode).filter(Boolean);
      if (!matches(item) && children.length === 0) return null;
      return { ...node, children: children.length ? children : undefined };
    };
    return buildCatalogTree(catalog).map(filterNode).filter(Boolean);
  }, [catalog, search, showInactive]);

  // ── editors ──
  const openModule = module => {
    setModuleEdit(module || {});
    moduleForm.setFieldsValue({
      code: module?.code || '',
      name: module?.name || '',
      icon: module?.icon || undefined,
      sortOrder: module?.sortOrder ?? 100,
      isActive: module ? module.isActive : true,
    });
  };

  const saveModule = async () => {
    const v = await moduleForm.validateFields();
    runSave(() => AccessService.saveModule({ ...moduleEdit, ...v }), () => setModuleEdit(null));
  };

  const openPage = (page, moduleId) => {
    const p = page || { moduleId };
    setPageEdit(p);
    pageForm.setFieldsValue({
      moduleId: p.moduleId,
      parentPageId: p.parentPageId ?? null,
      code: p.code || '',
      name: p.name || '',
      routePattern: p.routePattern || '',
      icon: p.icon || undefined,
      pageType: p.pageType || 'PAGE',
      showInMenu: page ? p.showInMenu : true,
      landingPriority: p.landingPriority ?? null,
      sortOrder: p.sortOrder ?? 100,
      isActive: page ? p.isActive : true,
    });
  };

  const savePage = async () => {
    const v = await pageForm.validateFields();
    const doSave = () =>
      runSave(
        () => AccessService.savePage({ ...pageEdit, ...v, routePattern: v.routePattern?.trim() || null }),
        () => setPageEdit(null),
      );
    if (pageEdit?.id && pageEdit.isActive && !v.isActive && pageEdit.grantCount > 0) {
      Modal.confirm({
        title: 'Deactivate page?',
        content: `This page has ${pageEdit.grantCount} grants. Users will lose access to it immediately (menu, route and its permission keys).`,
        okText: 'Deactivate',
        okButtonProps: { danger: true },
        onOk: doSave,
      });
    } else {
      doSave();
    }
  };

  const pagePermissions = permPage ? (catalog?.permissions || []).filter(p => p.pageId === permPage.id) : [];

  const openPerm = perm => {
    setPermEdit(perm || {});
    permForm.setFieldsValue({
      code: perm?.code || '',
      name: perm?.name || '',
      kind: perm?.kind || 'BUTTON',
      deniedBehavior: perm?.deniedBehavior || 'Disable',
      sortOrder: perm?.sortOrder ?? 10,
      isActive: perm ? perm.isActive : true,
    });
  };

  const savePerm = async () => {
    const v = await permForm.validateFields();
    runSave(() => AccessService.savePermission({ ...permEdit, ...v, pageId: permPage.id }), () => setPermEdit(null));
  };

  const parentOptions = useMemo(() => {
    const moduleId = pageEdit?.moduleId;
    return (catalog?.pages || [])
      .filter(p => p.moduleId === moduleId && p.id !== pageEdit?.id)
      .map(p => ({ value: p.id, label: `${p.name} (${p.code})` }));
  }, [catalog, pageEdit]);

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, row) => {
        const item = row.type === 'module' ? row.module : row.page;
        return (
          <span className={item.isActive ? '' : 'ac-inactive'}>
            <span className="me-2">{renderMenuIcon(item.icon)}</span>
            {item.name}
            {!item.isActive && <Tag className="ms-2">inactive</Tag>}
            {row.type === 'page' && item.pageType === 'CONTAINER' && <Tag className="ms-2">group</Tag>}
          </span>
        );
      },
    },
    {
      title: 'Code / key prefix',
      key: 'code',
      render: (_, row) =>
        row.type === 'module' ? (
          <code>{row.module.code}</code>
        ) : (
          <code>{`${(catalog.modules.find(m => m.id === row.page.moduleId) || {}).code}.${row.page.code}`}</code>
        ),
    },
    { title: 'Route', key: 'route', render: (_, row) => (row.type === 'page' ? <code>{row.page.routePattern || '—'}</code> : null) },
    {
      title: 'Menu',
      key: 'menu',
      width: 70,
      render: (_, row) => (row.type === 'page' ? (row.page.showInMenu ? 'Yes' : 'Hidden') : null),
    },
    {
      title: 'Landing',
      key: 'landing',
      width: 80,
      render: (_, row) => (row.type === 'page' ? row.page.landingPriority ?? '' : null),
    },
    {
      title: 'Order',
      key: 'order',
      width: 70,
      render: (_, row) => (row.type === 'module' ? row.module.sortOrder : row.page.sortOrder),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      render: (_, row) =>
        row.type === 'module' ? (
          <div className="d-flex gap-2">
            {canEdit && (
              <Button size="small" onClick={() => openModule(row.module)}>
                Edit
              </Button>
            )}
            {canCreate && (
              <Button size="small" onClick={() => openPage(null, row.module.id)}>
                Add page
              </Button>
            )}
          </div>
        ) : (
          <div className="d-flex gap-2">
            {canEdit && (
              <Button size="small" onClick={() => openPage(row.page)}>
                Edit
              </Button>
            )}
            <Button size="small" onClick={() => setPermPage(row.page)}>
              Permissions & buttons ({row.perms.length})
            </Button>
          </div>
        ),
    },
  ];

  const permColumns = [
    { title: 'Key', dataIndex: 'permissionKey', render: v => <code>{v}</code> },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Kind', dataIndex: 'kind', width: 90, render: v => <Tag color={v === 'BUTTON' ? 'blue' : undefined}>{v}</Tag> },
    { title: 'When denied', dataIndex: 'deniedBehavior', width: 110 },
    { title: 'Active', dataIndex: 'isActive', width: 80, render: v => (v ? 'Yes' : <Tag>inactive</Tag>) },
    {
      title: '',
      width: 70,
      render: (_, perm) =>
        canEdit && (
          <Button size="small" onClick={() => openPerm(perm)}>
            Edit
          </Button>
        ),
    },
  ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="Adding a page or button here controls menus and UI controls. An API endpoint is protected only when its code requires the permission key, so ask a developer to wire new keys on the server."
      />
      <div className="ac-toolbar">
        <Input.Search placeholder="Search name, code or route" allowClear style={{ maxWidth: 320 }} onChange={e => setSearch(e.target.value)} />
        <span>
          <Switch size="small" checked={showInactive} onChange={setShowInactive} /> Show inactive
        </span>
        <Button onClick={load} loading={loading}>
          Refresh
        </Button>
        <span className="ac-spacer" />
        {canCreate && (
          <Button type="primary" onClick={() => openModule(null)}>
            Add module
          </Button>
        )}
      </div>
      {!canEdit && !canCreate && <div className="small text-muted mb-2">Read-only — changes require Modules & Pages Create/Edit permission.</div>}

      <Table
        rowKey="key"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={tree}
        pagination={false}
        rowClassName={row => (row.type === 'module' ? 'ac-module-row' : '')}
        expandable={{ expandedRowKeys: expandedKeys, onExpandedRowsChange: keys => setExpandedKeys(keys) }}
        scroll={{ x: 1000 }}
      />

      {/* Module editor */}
      <Modal open={!!moduleEdit} title={moduleEdit?.id ? 'Edit module' : 'Add module'} onCancel={() => setModuleEdit(null)} onOk={saveModule} okText="Save" confirmLoading={saving} destroyOnClose>
        <Form form={moduleForm} layout="vertical">
          <Form.Item name="code" label="Code" rules={CODE_RULES} extra="Part of every permission key. Cannot be changed after creation.">
            <Input disabled={!!moduleEdit?.id} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }, { max: 100 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="icon" label="Icon">
            <Select allowClear options={iconOptions} />
          </Form.Item>
          <Form.Item name="sortOrder" label="Menu order" rules={[{ required: true }]}>
            <InputNumber min={0} max={10000} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Page editor */}
      <Modal open={!!pageEdit} title={pageEdit?.id ? 'Edit page' : 'Add page'} onCancel={() => setPageEdit(null)} onOk={savePage} okText="Save" confirmLoading={saving} destroyOnClose width={640}>
        <Form form={pageForm} layout="vertical">
          <Form.Item name="moduleId" label="Module" rules={[{ required: true }]}>
            <Select disabled={!!pageEdit?.id} options={(catalog?.modules || []).map(m => ({ value: m.id, label: `${m.name} (${m.code})` }))} onChange={id => setPageEdit(p => ({ ...p, moduleId: id }))} />
          </Form.Item>
          <Form.Item name="parentPageId" label="Parent page (optional, for nested menus)">
            <Select allowClear options={parentOptions} />
          </Form.Item>
          <Form.Item name="code" label="Code" rules={CODE_RULES} extra="Permission keys become MODULE.CODE.VIEW / CREATE / EDIT / DELETE. Cannot be changed later.">
            <Input disabled={!!pageEdit?.id} />
          </Form.Item>
          <Form.Item name="name" label="Menu name" rules={[{ required: true }, { max: 100 }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="routePattern"
            label="Route"
            extra="React route, e.g. /vendor-master or /cell/:id. Must not be used by another active page."
            rules={[{ max: 200 }, { pattern: /^(\/.*)?$/, message: "Must start with '/'" }]}
          >
            <Input />
          </Form.Item>
          <div className="row">
            <div className="col-sm-6">
              <Form.Item name="pageType" label="Type">
                <Select options={[{ value: 'PAGE', label: 'Page' }, { value: 'CONTAINER', label: 'Menu group (no route)' }]} />
              </Form.Item>
            </div>
            <div className="col-sm-6">
              <Form.Item name="icon" label="Icon">
                <Select allowClear options={iconOptions} />
              </Form.Item>
            </div>
            <div className="col-sm-4">
              <Form.Item name="sortOrder" label="Menu order" rules={[{ required: true }]}>
                <InputNumber min={0} max={10000} />
              </Form.Item>
            </div>
            <div className="col-sm-4">
              <Form.Item name="landingPriority" label="Landing priority" extra="Lowest wins after login">
                <InputNumber min={0} max={10000} />
              </Form.Item>
            </div>
            <div className="col-sm-2">
              <Form.Item name="showInMenu" label="In menu" valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            <div className="col-sm-2">
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
          </div>
          {!pageEdit?.id && <div className="small text-muted">View, Create, Edit and Delete permissions are created automatically.</div>}
        </Form>
      </Modal>

      {/* Permissions & buttons of a page */}
      <Modal open={!!permPage} title={`Permissions & buttons — ${permPage?.name || ''}`} onCancel={() => setPermPage(null)} footer={null} width={860} destroyOnClose>
        <div className="ac-toolbar">
          <span className="ac-spacer" />
          {canCreate && (
            <Button type="primary" onClick={() => openPerm(null)}>
              Add button / action
            </Button>
          )}
        </div>
        <Table rowKey="id" size="small" columns={permColumns} dataSource={pagePermissions} pagination={false} />
      </Modal>

      <Modal open={!!permEdit} title={permEdit?.id ? 'Edit permission' : 'Add button / action'} onCancel={() => setPermEdit(null)} onOk={savePerm} okText="Save" confirmLoading={saving} destroyOnClose>
        <Form form={permForm} layout="vertical">
          <Form.Item name="code" label="Code" rules={CODE_RULES} extra="e.g. PRINT, APPROVE, EXPORT, RELEASE. Cannot be changed later.">
            <Input disabled={!!permEdit?.id} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }, { max: 100 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="kind" label="Kind">
            <Select disabled options={[{ value: 'BUTTON', label: 'Button / custom action' }, { value: 'CRUD', label: 'CRUD' }]} />
          </Form.Item>
          <Form.Item name="deniedBehavior" label="When the user does not have it">
            <Select options={[{ value: 'Hide', label: 'Hide the control' }, { value: 'Disable', label: 'Show it disabled' }]} />
          </Form.Item>
          <Form.Item name="sortOrder" label="Order">
            <InputNumber min={0} max={10000} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CatalogTab;
